import { Delete } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { DatePicker, TimeField } from '@mui/x-date-pickers-pro';
import { isSameDay } from 'date-fns';
import { useState } from 'react';

type DateRangeEditProps = {
  readonly idx: number;
  readonly start: Date | undefined | null;
  readonly end: Date | undefined | null;
  readonly onChange: (index: number, start: Date | null, end: Date | null) => void;
  readonly onRemove: (index: number) => void;
  readonly showRemoveButton: boolean;
  readonly unavailableDates: Date[];
  readonly disableDateEdit: boolean;
};

export const TimeRangeEdit = (props: DateRangeEditProps) => {
  const [start, setStart] = useState(props.start ?? null);
  const [end, setEnd] = useState(props.end ?? null);
  const { idx, unavailableDates, showRemoveButton, disableDateEdit } = props;

  const shouldDisableDate = (date: Date): boolean =>
    unavailableDates.some((unavailable) => isSameDay(date, unavailable));

  const handleDayChange = (newValue: Date | null): void => {
    if (!newValue || !end || !start) return;

    const newStart = newValue;
    const newEnd = new Date(newValue);
    newEnd.setHours(end.getHours(), end.getMinutes(), end.getSeconds(), end.getMilliseconds());

    props.onChange(idx, newStart, newEnd);
  };

  const handleBlur = (): void => {
    const [arrangedStart, arrangedEnd] = arrangeTimes(start, end);
    props.onChange(idx, arrangedStart, arrangedEnd);
  };

  return (
    <Box display="flex" flexDirection="row" gap={2} flexWrap="wrap">
      <DatePicker
        disabled={disableDateEdit}
        shouldDisableDate={shouldDisableDate}
        label="Datum"
        value={start}
        sx={{ maxWidth: '160px' }}
        slotProps={{
          textField: {
            inputProps: {
              disabled: true,
              'data-testid': `timerange-date-input-${idx}`,
            },
          },
        }}
        onAccept={handleDayChange}
      />

      <Box display="flex" flexDirection="row" gap={2}>
        <TimeField
          data-testid={`start-${idx}`}
          size="small"
          sx={{ maxWidth: '100px' }}
          label="Start"
          value={start}
          format="HH:mm"
          onChange={(newValue) => setStart(newValue)}
          onBlur={handleBlur}
        />
        <TimeField
          data-testid={`ende-${idx}`}
          label="Ende"
          sx={{ maxWidth: '100px' }}
          value={end}
          format="HH:mm"
          onChange={(newValue) => setEnd(newValue)}
          onBlur={handleBlur}
        />
        {showRemoveButton && (
          <Button
            data-testid={`timerange-remove-icon-${idx}`}
            size="small"
            onClick={() => props.onRemove(idx)}
            sx={{ height: '36px' }}
          >
            <Delete fontSize="small" />
          </Button>
        )}
      </Box>
    </Box>
  );
};

function arrangeTimes(startTime: Date | null, endTime: Date | null): [Date | null, Date | null] {
  if (!startTime && !endTime) {
    return [null, null];
  }

  if (!startTime) {
    return [null, endTime];
  }

  if (!endTime) {
    return [startTime, null];
  }

  if (startTime > endTime) {
    return [endTime, startTime];
  } else {
    return [startTime, endTime];
  }
}
